import { FC, useEffect, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Alert,
  Box,
  Checkbox,
  FormInputText,
  Modal,
  Svg,
  SvgBubble,
  Text,
  Tooltip,
} from '@hopskipdrive/react-components';
import { Button } from '@rideiq/shared/components/Button';
import { colorTokens } from '@rideiq/shared/components/variables';
import { FormA11yErrorAnnouncer } from '@rideiq/shared/features/FormA11yErrorAnnouncer';
import { differenceInYears } from 'date-fns';
import * as Yup from 'yup';

import { RiderServices } from '../../../components/RiderServices';
import { useRiderQuery } from '../../../hooks/useRiderQuery';
import { Divider, InputLabel, MarginLeft } from '../SafetySettings.styles';
import { ISafetySettings } from '../types';

interface IModal {
  isOpen: boolean;
  onSubmit: (data) => void;
  onCancel: () => void;
  safetySettings: ISafetySettings;
  dateOfBirth: string;
  isSubmitting: boolean;
  riderId: string;
}

export const EditModal: FC<IModal> = ({
  isOpen,
  onSubmit,
  onCancel,
  safetySettings,
  dateOfBirth,
  isSubmitting,
  riderId,
}) => {
  return (
    <Modal open={isOpen} data-testid="rider-safety-settings-modal">
      <Modal.Title>Safety Settings</Modal.Title>
      <Modal.Content>
        <Form
          safetySettings={safetySettings}
          onSubmit={onSubmit}
          dateOfBirth={dateOfBirth}
          onCancel={onCancel}
          riderId={riderId}
        />
      </Modal.Content>
      <Modal.Actions>
        <Button color="tertiary" onClick={onCancel} loading={isSubmitting}>
          Cancel
        </Button>
        <Button
          aria-live="polite"
          aria-atomic="true"
          color="primary"
          type="submit"
          form={'safety-settings'}
          loading={isSubmitting}
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

const CheckboxGroup = ({ control, age }) => (
  <Box display="flex" flexDirection="column">
    <Controller
      control={control}
      name="frontSeatAuthorized"
      render={({ field: { value, onChange, onBlur } }) => {
        return (
          <label htmlFor="frontSeatAuthorized">
            <Checkbox
              data-testid="frontSeatAuthorized"
              onBlur={onBlur}
              checked={value}
              disabled={cannotSitUpFront(age)}
              onChange={(e) => {
                onChange(e.target.checked);
              }}
            />
            Can sit up front
          </label>
        );
      }}
    />
    <Controller
      control={control}
      name="boosterSeat"
      render={({ field: { value, onChange, onBlur } }) => {
        return (
          <label htmlFor="boosterSeat">
            <Checkbox
              data-testid="boosterSeat"
              onBlur={onBlur}
              checked={value}
              onChange={(e) => {
                onChange(e.target.checked);
              }}
            />
            Needs a booster seat
          </label>
        );
      }}
    />
  </Box>
);

const CodeWord = ({ errors, control }) => (
  <Box display="flex" alignItems="center">
    <InputLabel fieldtype="required" htmlFor="codeWord">
      Code word
    </InputLabel>
    <Controller
      control={control}
      name="codeWord"
      render={({ field: { value, onChange } }) => (
        <FormInputText
          id="codeWord"
          name="codeWord"
          value={value}
          variant="outlined"
          error={!!errors.codeWord}
          helperText={errors.codeWord ? errors.codeWord.message : ''}
          onChange={onChange}
        />
      )}
    />
    <MarginLeft>
      <Tooltip title="Create a secret code word that your rider can remember. The CareDriver will say this at pick-up so your rider knows they are with the right person.">
        <SvgBubble>
          <Svg icon="question" />
        </SvgBubble>
      </Tooltip>
    </MarginLeft>
  </Box>
);

const RiderNotes = ({ errors, control, safetySettings }) => {
  return (
    <Box
      display={safetySettings.permanentRiderNote ? 'flex' : ''}
      justifyContent="space-between"
      alignItems="flex-start"
    >
      <Box display="flex" alignItems={safetySettings.permanentRiderNote ? 'left' : 'center'}>
        <InputLabel style={{ marginBottom: '160px' }} htmlFor="riderNotes">
          Rider notes
        </InputLabel>
        <Box display="flex" flexDirection="column" alignItems="flex-end">
          <Controller
            control={control}
            name="riderNotes"
            render={({ field: { value, onChange } }) => (
              <FormInputText
                sx={safetySettings.permanentRiderNote ? { width: '272px', ml: '40px' } : { width: '352px' }}
                id="riderNotes"
                placeholder="Ex: Non-speaking, sensitive to loud noise. Use child lock on door and do not play any music."
                multiline
                rows={4}
                name="riderNotes"
                value={value}
                variant="outlined"
                error={!!errors.riderNotes}
                helperText={(() => {
                  if (errors.riderNotes) {
                    return errors.riderNotes.message;
                  }
                  if (!value || value.length === 0) {
                    return <span style={{ color: '#6B7280' }}>Max. 500 characters</span>;
                  }

                  return (
                    <span>
                      {value.length >= 500
                        ? `Max. ${Math.min(value.length, 500)} characters`
                        : `${Math.min(value.length, 500)}/500 characters`}
                    </span>
                  );
                })()}
                onChange={onChange}
              />
            )}
          />
          <Text
            variant="caption"
            textColor="secondaryText"
            mt={2}
            sx={{
              background: colorTokens.background.light,
              width: safetySettings.permanentRiderNote ? { width: '272px', ml: '40px' } : { width: '352px' },
              padding: '12px',
              textAlign: 'left',
            }}
          >
            Rider notes should highlight crucial rider-specific details and actions required to enable safety and
            support for the rider. Notes should be concise, complete, and actionable.
          </Text>
        </Box>
      </Box>

      {safetySettings.permanentRiderNote && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="flex-start"
          mt={2}
          style={{ width: '50%', height: '100px' }}
        >
          <Box style={{ width: '100%', height: '100%', overflowY: 'scroll', overflowX: 'hidden' }}>
            <Text style={{ marginLeft: '16px', width: '95%' }}>{safetySettings.permanentRiderNote}</Text>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const validationSchema = Yup.object().shape({
  codeWord: Yup.string().required('Required Field').max(34, 'Code word exceeds 34 character max'),
  riderNotes: Yup.string()
    .nullable()
    .test('rider notes validation', 'Max. 500 characters', function riderNotesTest(value) {
      const context = this.options.context as { originalRiderNotes: string };
      const originalRiderNotes = context.originalRiderNotes;
      const wasChanged = value !== originalRiderNotes;

      if (!wasChanged) {
        return true; // If the Rider notes weren't changed, skip validation
      }

      return !value || value.length <= 500;
    }),
});

const cannotSitUpFront = (age) => {
  if (!isNaN(age)) {
    if (age <= 11) {
      return true;
    }
  }

  return false;
};

const Form = ({ safetySettings, onSubmit, dateOfBirth, onCancel, riderId }) => {
  const { riderDetails, fetchRiderBySlug } = useRiderQuery();

  useEffect(() => {
    riderId && fetchRiderBySlug(riderId);
  }, [riderId, fetchRiderBySlug]);

  const methods = useForm<ISafetySettings>({
    defaultValues: useMemo(() => {
      return {
        codeWord: safetySettings.codeWord,
        riderNotes: safetySettings.riderNotes,
        permanentRiderNote: safetySettings.permanentRiderNote,
        boosterSeat: safetySettings.boosterSeat,
        frontSeatAuthorized: safetySettings.frontSeatAuthorized,
        mustBeMet: safetySettings.mustBeMet,
        services: safetySettings.services,
      };
    }, [safetySettings]),
    context: { originalRiderNotes: safetySettings.riderNotes },
    resolver: yupResolver(validationSchema),
  });
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { isDirty, errors },
  } = methods;

  const age = differenceInYears(Date.now(), new Date(dateOfBirth));
  const [boosterSeat, frontSeatAuthorized] = watch(['boosterSeat', 'frontSeatAuthorized']);

  useEffect(() => {
    if (age >= 12 && boosterSeat) {
      setValue('frontSeatAuthorized', false, { shouldDirty: false });
    }
  }, [boosterSeat]);

  useEffect(() => {
    if (age >= 12 && frontSeatAuthorized) {
      setValue('boosterSeat', false, { shouldDirty: false });
    }
  }, [frontSeatAuthorized]);

  return (
    <>
      <FormA11yErrorAnnouncer errors={errors} />
      <form
        id="safety-settings"
        aria-label="form"
        onSubmit={handleSubmit((data: ISafetySettings) => (isDirty ? onSubmit(data) : onCancel()))}
      >
        <CheckboxGroup control={control} age={age} />
        <Alert
          sx={{ m: '10px 0' }}
          severity="info"
          message={
            <span>
              The <b>Must Be Met</b> requirement is now location-specific! You’ll be able to enable <b>Must Be Met</b>{' '}
              in later steps, or navigate to this rider’s assigned locations to enable or disable <b>Must Be Met</b>.
            </span>
          }
        />
        <Divider />
        <CodeWord control={control} errors={errors} />
        <Divider />
        <RiderNotes control={control} errors={errors} safetySettings={safetySettings} />
        <FormProvider {...methods}>
          <RiderServices
            editFlow={true}
            isInitialized={false}
            setIsInitialized={() => {}}
            riderState={riderDetails?.state}
            dateOfBirth={dateOfBirth}
          />
        </FormProvider>
      </form>
    </>
  );
};
